import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { useNavLinkClick } from "../hooks/useNavLinkClick";
import { Link } from "react-router-dom";
import { HelpCircle, Plus, Minus } from "lucide-react";
import CTA02 from "../components/CTA02Section";
import { DynamicNavigation, Footer } from "../components";
import { useSeo } from "../seo";

function SilkWaveCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let tick = 0;
    let mouse = { x: 0, y: 0, active: false };
    let W = 0, H = 0;

    const resize = () => {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };

    const s43 = (n: number) => { const x = Math.sin(n) * 43758.5453; return x - Math.floor(x); };
    const n1 = (x: number) => { const i = Math.floor(x), f = x - i, u = f * f * (3 - 2 * f); return s43(i) * (1 - u) + s43(i + 1) * u; };
    const fbm = (x: number) => n1(x) * 0.65 + n1(x * 2.07 + 4.1) * 0.35;

    const COLS: [string, string][] = [
      ["#6366f1", "#818cf8"],
      ["#8b5cf6", "#a78bfa"],
      ["#3b82f6", "#93c5fd"],
      ["#06b6d4", "#67e8f9"],
      ["#10b981", "#6ee7b7"],
      ["#ec4899", "#f9a8d4"],
      ["#a78bfa", "#ddd6fe"],
    ];
    const ribbons = COLS.map((cols, i) => ({
      offset: i * 1.41 + Math.random() * 2.5,
      speed: 0.22 + i * 0.035 + Math.random() * 0.06,
      thick: 72 + i * 15,
      alpha: 0.28 + (i % 3) * 0.07,
      yBase: (i + 0.5) / COLS.length,
      c1: cols[0],
      c2: cols[1],
    }));

    const SEGS = 55;

    const draw = () => {
      tick++;
      const t = tick * 0.0009;
      ctx.clearRect(0, 0, W, H);

      for (const r of ribbons) {
        const mx = mouse.active ? mouse.x / W : 0.5;
        const my = mouse.active ? mouse.y / H : r.yBase;
        const pull = mouse.active ? 0.13 : 0;

        const pts: [number, number][] = [];
        for (let s = 0; s <= SEGS; s++) {
          const nx = s / SEGS;
          const wave = (fbm(nx * 2.3 + r.offset + t * r.speed) - 0.5) * 0.3;
          const warp = pull * Math.exp(-((nx - mx) ** 2) * 22) * (my - r.yBase);
          pts.push([nx * W, (r.yBase + wave + warp) * H]);
        }

        const hGrad = ctx.createLinearGradient(0, 0, W, 0);
        const phase = (Math.sin(t * r.speed * 2 + r.offset) + 1) / 2;
        hGrad.addColorStop(0,     `${phase > 0.5 ? r.c2 : r.c1}cc`);
        hGrad.addColorStop(phase, `${phase > 0.5 ? r.c1 : r.c2}ff`);
        hGrad.addColorStop(1,     `${phase > 0.5 ? r.c2 : r.c1}cc`);

        ctx.save();
        ctx.globalAlpha = r.alpha;
        ctx.filter = `blur(${Math.round(r.thick * 0.28)}px)`;
        ctx.strokeStyle = hGrad;
        ctx.lineWidth = r.thick * 0.55;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (let s = 1; s < pts.length - 1; s++) {
          const cpx = (pts[s][0] + pts[s + 1][0]) / 2;
          const cpy = (pts[s][1] + pts[s + 1][1]) / 2;
          ctx.quadraticCurveTo(pts[s][0], pts[s][1], cpx, cpy);
        }
        ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]);
        ctx.stroke();
        ctx.restore();
      }

      animId = requestAnimationFrame(draw);
    };

    const onMove = (cx: number, cy: number) => {
      const rect = canvas.getBoundingClientRect();
      mouse = { x: cx - rect.left, y: cy - rect.top, active: true };
    };
    const onLeave = () => { mouse = { ...mouse, active: false }; };

    resize(); draw();
    window.addEventListener("resize", resize);
    canvas.addEventListener("mousemove", (e) => onMove(e.clientX, e.clientY));
    canvas.addEventListener("mouseleave", onLeave);
    canvas.addEventListener("touchmove", (e) => { onMove(e.touches[0].clientX, e.touches[0].clientY); }, { passive: true });
    canvas.addEventListener("touchend", onLeave);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: "block", willChange: "transform" }}
    />
  );
}

const faqItems = [
  {
    q: "Is Subclass 190 a permanent visa?",
    a: "Yes. The Subclass 190 Skilled Nominated Visa grants you and eligible family members full Australian permanent residency from the date of grant.",
  },
  {
    q: "Do I need to stay in the nominating state?",
    a: "You are generally expected to live and work in the nominating state or territory for around two years, based on your nomination commitment. This expectation may vary by state or territory and is typically a nomination condition rather than a direct visa condition. Some states actively monitor compliance.",
  },
  {
    q: "Can I include my family in the application?",
    a: "Yes. Eligible dependants — including your spouse or de facto partner and dependent children — can be included in your application and receive Australian permanent residency alongside you.",
  },
  {
    q: "How does state nomination affect my points?",
    a: "State or territory nomination adds 5 points to your SkillSelect points score. This can improve your likelihood of receiving an Invitation to Apply (ITA) through the SkillSelect system, though an invitation is not guaranteed.",
  },
  {
    q: "How do I get state nomination for Subclass 190?",
    a: "Each state and territory publishes its own skilled occupation list and sets its own nomination criteria. You must submit a separate Expression of Interest directly to your chosen state or territory. Available occupations and criteria change regularly, so check directly with the relevant state authority.",
  },
  {
    q: "How long does Subclass 190 processing take?",
    a: "Processing times vary based on occupation, application quality, health examinations, police clearances, and current government demand. Check current official processing times on the Department of Home Affairs website, or speak with a registered migration agent for guidance tailored to your situation.",
  },
  {
    q: "What is the minimum points score for Subclass 190?",
    a: "The minimum threshold is 65 points, but meeting the minimum does not guarantee an invitation. Competitive applicants typically score higher. State nomination adds 5 points to your total.",
  },
];

function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="py-24 bg-white px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className="lg:sticky lg:top-24">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 mb-6"
            >
              <HelpCircle className="w-3 h-3 text-indigo-600" />
              <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Self Service Help</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-black mb-8 tracking-tighter leading-[1.1] text-gray-900"
            >
              Your questions,{" "}
              <br />
              <span className="text-indigo-600 italic underline decoration-indigo-200 underline-offset-8">
                answered.
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="text-zinc-500 text-sm max-w-sm mb-10 italic leading-relaxed"
            >
              Can't find what you're looking for? Reach out to our migration specialists for tailored advice on Subclass 190.
            </motion.p>
            <motion.img
              src="/assets/faq/faq-image.png"
              alt="FAQ support illustration"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="w-full max-w-sm mt-4"
            />
          </div>
          <div className="space-y-4">
            {faqItems.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={`rounded-[2rem] border transition-all duration-500 overflow-hidden ${
                  open === i
                    ? "border-indigo-200 bg-white shadow-xl"
                    : "border-zinc-200 bg-zinc-50/50"
                }`}
              >
                <button
                  className="w-full flex items-center justify-between p-8 text-left"
                  onClick={() => setOpen(open === i ? null : i)}
                >
                  <span className="text-base md:text-lg font-black tracking-tight pr-6 text-gray-900">{faq.q}</span>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                    open === i ? "bg-indigo-600 text-white" : "bg-zinc-200 text-zinc-500"
                  }`}>
                    {open === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </div>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 pb-8">
                        <div className="h-px bg-zinc-200 mb-6 opacity-60" />
                        <p className="text-zinc-500 text-sm leading-relaxed max-w-lg italic">{faq.a}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AustraliaSkilledNominatedVisa190() {
  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "services", label: "Services", href: "/services" },
    { id: "about", label: "About", href: "/about" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];

  const onLinkClick = useNavLinkClick(links);

  useSeo({
    title: "Skilled Nominated Visa Subclass 190 Australia | State Nomination PR Visa",
    description: "The Subclass 190 Skilled Nominated Visa grants Australian permanent residency to skilled workers nominated by a state or territory government. Check eligibility, points requirements, processing times, and apply today.",
    path: "/australia-skilled-nominated-visa-190",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Skilled Nominated Visa 190", url: "/australia-skilled-nominated-visa-190" },
    ],
    faqItems: faqItems.map((f) => ({ question: f.q, answer: f.a })),
  });

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap" rel="stylesheet" />
      </Helmet>

      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Logo" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
        />
      </header>

      {/* HERO SECTION */}
      <section
        className="relative flex flex-col items-center px-4 pb-20 md:pb-32 lg:pb-40 pt-24 md:pt-36 lg:pt-44 overflow-hidden min-h-[520px]"
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: "linear-gradient(145deg, #f8faff 0%, #eef2ff 25%, #e0e7ff 50%, #ede9fe 75%, #f5f3ff 100%)",
        }}
      >
        <SilkWaveCanvas />
        <h1 className="relative z-10 text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-center font-semibold max-w-3xl md:max-w-4xl mt-4 md:mt-8 px-2 text-gray-900 leading-tight">
          Skilled Nominated Visa Subclass 190
        </h1>
        <p className="relative z-10 text-gray-600 text-sm sm:text-base md:text-lg text-center max-w-xl md:max-w-2xl mt-4 md:mt-5 px-4">
          The Skilled Nominated Visa (Subclass 190) is a permanent residency visa for skilled workers nominated by an Australian state or territory government. State nomination adds 5 points to your SkillSelect score, opening a direct pathway to Australian PR.
        </p>
      </section>

      {/* INTRO SECTION */}
      <section className="py-16 px-4 bg-blue-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            What is the Skilled Nominated Visa Subclass 190?
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            The <strong>Skilled Nominated Visa (Subclass 190)</strong> is a permanent residency visa for skilled professionals nominated by an Australian state or territory government. Unlike the{" "}
            <Link to="/australia-skilled-independent-visa-189" className="text-indigo-600 underline underline-offset-2 hover:text-indigo-800">Subclass 189</Link>, which is independent of any sponsor, the 190 requires formal state or territory nomination — and in return, that nomination adds 5 extra points to your SkillSelect score.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            Each state and territory manages its own skilled migration programme and publishes occupation lists that reflect local workforce needs. By accepting nomination, you make a commitment to live and work in that state or territory for approximately two years. This is generally a nomination commitment rather than a direct visa condition, and requirements can vary by state or territory.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed mb-8">
            The Subclass 190 is well suited to skilled migrants whose occupation is in demand at a state level, or those seeking to improve their invitation chances by leveraging a state-nominated pathway through SkillSelect.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Visa Type", value: "Permanent Residency" },
              { label: "Points Minimum", value: "65 Points" },
              { label: "Nomination Bonus", value: "+5 Points" },
              { label: "Family Included", value: "Yes — eligible dependants" },
            ].map((fact) => (
              <div key={fact.label} className="bg-white rounded-xl p-4 border border-indigo-100 text-center shadow-sm">
                <p className="text-xs text-indigo-600 font-semibold uppercase tracking-wide mb-1">{fact.label}</p>
                <p className="text-sm font-bold text-gray-900">{fact.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="py-16 px-8 lg:px-20 xl:px-[120px] bg-white">
        <div className="max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-12 tracking-tight">
            Benefits of Subclass 190 Visa
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              {
                title: "Direct Permanent Residency",
                desc: "Receive permanent resident status from the date the visa is granted.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><circle cx="22" cy="22" r="20" stroke="#6366f1" strokeWidth="2"/><path d="M14 22l6 6 10-12" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>),
              },
              {
                title: "State Nomination Bonus",
                desc: "State nomination adds 5 extra points to your SkillSelect score.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><path d="M22 6l4 10h10l-8 6 3 10-9-6-9 6 3-10-8-6h10z" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round"/></svg>),
              },
              {
                title: "Higher Invitation Chances",
                desc: "State nomination can improve your invitation chances by increasing your points score and opening state-based invitation pathways. An invitation is not guaranteed.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><path d="M8 36l10-14 8 8 8-20 8 26" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>),
              },
              {
                title: "Full Work Rights",
                desc: "Full work rights as an Australian permanent resident, with flexibility to work across Australia while honouring your state nomination commitment.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><rect x="10" y="16" width="24" height="18" rx="3" stroke="#6366f1" strokeWidth="2"/><path d="M16 16v-4a6 6 0 0 1 12 0v4" stroke="#6366f1" strokeWidth="2"/></svg>),
              },
              {
                title: "Medicare Access",
                desc: "Access Australia's public healthcare system, subject to eligibility.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><rect x="10" y="10" width="24" height="24" rx="4" stroke="#6366f1" strokeWidth="2"/><path d="M22 16v12M16 22h12" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round"/></svg>),
              },
              {
                title: "Family Inclusion",
                desc: "Include eligible dependants in your visa application.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><circle cx="16" cy="16" r="5" stroke="#6366f1" strokeWidth="2"/><circle cx="28" cy="16" r="5" stroke="#6366f1" strokeWidth="2"/><path d="M8 36c0-6 4-9 8-9s6 2 6 2 2-2 6-2 8 3 8 9" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/></svg>),
              },
              {
                title: "Citizenship Pathway",
                desc: "Apply for Australian citizenship when eligibility conditions are met.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><circle cx="22" cy="22" r="14" stroke="#6366f1" strokeWidth="2"/><path d="M22 8v28M8 22h28" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round"/></svg>),
              },
              {
                title: "Travel Flexibility",
                desc: "Travel in and out of Australia under the visa travel facility rules.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><path d="M22 10c-7 0-14 5-14 14 0 8 7 12 14 12s14-4 14-12c0-9-7-14-14-14z" stroke="#6366f1" strokeWidth="2"/><path d="M8 24h28M16 12a24 24 0 0 0 0 20M28 12a24 24 0 0 1 0 20" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round"/></svg>),
              },
              {
                title: "Career Freedom",
                desc: "Change employers, industries, or locations freely within Australia.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><path d="M14 22h16M26 16l6 6-6 6" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>),
              },
              {
                title: "Long-Term Settlement",
                desc: "Build your future in Australia through work, study, and family pathways.",
                icon: (<svg width="44" height="44" viewBox="0 0 44 44" fill="none"><path d="M12 36V20l10-10 10 10v16" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><rect x="18" y="26" width="8" height="10" rx="1" stroke="#6366f1" strokeWidth="2"/></svg>),
              },
            ].map((benefit) => (
              <div
                key={benefit.title}
                className="pt-8 pb-6 px-2 flex flex-col gap-5 transition-all duration-200 cursor-pointer group"
              >
                <div className="mb-1">{benefit.icon}</div>
                <div className="flex-1">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 leading-snug">{benefit.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{benefit.desc}</p>
                </div>
                <div className="border-b border-dashed border-zinc-300 mt-2" />
              </div>
            ))}
          </div>
          <p className="mt-8 text-gray-500 text-sm italic">
            The Subclass 190 visa is a strong pathway to permanent residency for skilled professionals with state or territory nomination.
          </p>
        </div>
      </section>

      {/* ELIGIBILITY SECTION */}
      <section className="bg-white px-8 lg:px-20 xl:px-[120px] py-20 flex flex-col items-center">
        <div className="max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl w-full mb-9">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-zinc-800 mb-4 tracking-tight">
            Subclass 190 Visa Eligibility Requirements
          </h2>
          <p className="text-base md:text-lg font-medium text-zinc-600 tracking-tight max-w-xl">
            To apply for the Skilled Nominated Visa Subclass 190, applicants usually need to meet these requirements:
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl w-full border-t border-l border-zinc-200">
          {[
            {
              title: "Age Under 45",
              desc: "Be under 45 years of age at the time of invitation to ensure eligibility.",
              icon: (<svg width="17" height="17" viewBox="0 0 17 17" fill="none"><path d="M8.25 15.75a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15" stroke="#4f39f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M13.598 3.066C10.665 6.104 6.75 7.08.938 7.454m14.625 1.429c-4.966-1.057-9.106.75-12.285 4.74" stroke="#4f39f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M5.67 1.313c3.278 4.5 4.5 7.065 6 13.29" stroke="#4f39f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>),
              active: true,
            },
            {
              title: "State or Territory Nomination",
              desc: "Receive a formal nomination from an Australian state or territory government.",
              icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><g clipPath="url(#stn)" stroke="#4f39f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8.553 1.452a1.33 1.33 0 0 0-1.106 0l-5.714 2.6a.667.667 0 0 0 0 1.22l5.72 2.607a1.33 1.33 0 0 0 1.107 0l5.72-2.6a.667.667 0 0 0 0-1.22z"/><path d="M1.333 8a.67.67 0 0 0 .387.607l5.733 2.606a1.33 1.33 0 0 0 1.1 0l5.72-2.6A.67.67 0 0 0 14.667 8"/><path d="M1.333 11.332a.67.67 0 0 0 .387.607l5.733 2.606a1.33 1.33 0 0 0 1.1 0l5.72-2.6a.67.67 0 0 0 .394-.613"/></g><defs><clipPath id="stn"><path fill="#fff" d="M0 0h16v16H0z"/></clipPath></defs></svg>),
            },
            {
              title: "Skilled Occupation",
                desc: "Your occupation must be eligible under the relevant skilled migration framework and available for nomination by your chosen state or territory. Occupation lists are updated regularly.",
              icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><g clipPath="url(#occ190)" stroke="#4f39f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9.8 4.201a.667.667 0 0 0 0 .933l1.067 1.067a.666.666 0 0 0 .933 0l2.07-2.07c.214-.215.576-.147.656.145A4 4 0 0 1 9.02 8.981l-5.273 5.274a1.414 1.414 0 0 1-2-2L7.021 6.98a4 4 0 0 1 4.704-5.506c.292.08.36.441.146.656z"/></g><defs><clipPath id="occ190"><path fill="#fff" d="M0 0h16v16H0z"/></clipPath></defs></svg>),
            },
            {
              title: "Skills Assessment",
              desc: "Obtain a positive skills assessment from the relevant assessing authority for your occupation.",
              icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2.667 9.334a.667.667 0 0 1-.52-1.087l6.6-6.8a.333.333 0 0 1 .573.307L8.04 5.767a.667.667 0 0 0 .627.9h4.666a.666.666 0 0 1 .52 1.087l-6.6 6.8a.334.334 0 0 1-.573-.307l1.28-4.013a.667.667 0 0 0-.627-.9z" stroke="#4f39f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>),
            },
            {
              title: "Points Score (65+)",
              desc: "Meet the minimum points threshold under the General Skilled Migration points test.",
              icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2v12M2 8h12" stroke="#4f39f6" strokeWidth="1.5" strokeLinecap="round"/></svg>),
            },
            {
              title: "English Proficiency",
              desc: "Demonstrate competent English through IELTS, PTE, TOEFL, or an equivalent test.",
              icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><g clipPath="url(#eng190)"><path d="M8 14.665A6.667 6.667 0 1 0 8 1.332a6.667 6.667 0 0 0 0 13.333"/><path d="M8 1.332a9.667 9.667 0 0 0 0 13.333A9.667 9.667 0 0 0 8 1.332M1.333 8h13.334" stroke="#4f39f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></g><defs><clipPath id="eng190"><path fill="#fff" d="M0 0h16v16H0z"/></clipPath></defs></svg>),
            },
            {
              title: "Submit EOI",
              desc: "Lodge an Expression of Interest (EOI) through SkillSelect to be considered for an invitation.",
              icon: (<svg width="20" height="16" viewBox="0 0 20 16" fill="none"><path d="M16.05.75H3.45a1.8 1.8 0 0 0-1.8 1.8v7.2a1.8 1.8 0 0 0 1.8 1.8h12.6a1.8 1.8 0 0 0 1.8-1.8v-7.2a1.8 1.8 0 0 0-1.8-1.8M.75 15.148h18" stroke="#4f39f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>),
            },
            {
              title: "Health & Character",
              desc: "Pass health examinations and meet character requirements set by the Australian Government.",
              icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="m10.667 12 4-4-4-4M5.333 4l-4 4 4 4" stroke="#4f39f6" strokeLinecap="round" strokeLinejoin="round"/></svg>),
            },
          ].map((item, i) => (
            <div
              key={item.title}
              className={`relative p-6 md:p-8 flex flex-col gap-4 border-r border-b border-zinc-200 transition-all duration-300 cursor-pointer ${
                item.active ? "bg-gradient-to-b from-white to-[#EEF0FF]" : "bg-white hover:bg-gradient-to-b hover:from-white hover:to-[#EEF0FF]"
              }`}
            >
              {i === 1 && <div className="absolute left-0 top-12 bottom-12 md:top-16 md:bottom-16 w-1.5 bg-indigo-500 rounded-r" />}
              <div className="flex items-center gap-2.5 mb-1">
                <div>{item.icon}</div>
                <h3 className="text-base md:text-lg font-semibold text-zinc-800 leading-snug">{item.title}</h3>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed mb-4">{item.desc}</p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-zinc-500 text-sm italic max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl w-full">
          Eligibility for Subclass 190 depends on age, state nomination, occupation demand, English score, and total migration points.
        </p>
      </section>

      {/* POINTS TEST SECTION */}
      <section className="py-20 px-8 lg:px-20 xl:px-[120px] bg-blue-50">
        <div className="max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            The Points Test for Subclass 190
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-10 max-w-3xl">
            The Subclass 190 visa uses Australia's General Skilled Migration (GSM) points test. A minimum of 65 points is required to be eligible, but in practice, competitive applicants score significantly higher to receive an invitation. State nomination contributes 5 points to your total.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-8 mb-10">
            {[
              { category: "Age", rows: [{ label: "18–24 yrs", pts: "25 pts" }, { label: "25–32 yrs", pts: "30 pts" }, { label: "33–39 yrs", pts: "25 pts" }, { label: "40–44 yrs", pts: "15 pts" }] },
              { category: "English Language", rows: [{ label: "Competent", pts: "+0 pts" }, { label: "Proficient (IELTS 7+)", pts: "+10 pts" }, { label: "Superior (IELTS 8+)", pts: "+20 pts" }] },
              { category: "Overseas Work Experience", rows: [{ label: "3–5 years", pts: "+5 pts" }, { label: "5–8 years", pts: "+10 pts" }, { label: "8+ years", pts: "+15 pts" }] },
              { category: "Australian Work Experience", rows: [{ label: "1–3 years", pts: "+5 pts" }, { label: "3–5 years", pts: "+10 pts" }, { label: "5–8 years", pts: "+15 pts" }, { label: "8+ years", pts: "+20 pts" }] },
              { category: "Education", rows: [{ label: "Doctoral degree", pts: "+20 pts" }, { label: "Bachelor's or Master's", pts: "+15 pts" }, { label: "Diploma or trade qual.", pts: "+10 pts" }] },
              { category: "Australian Study Requirement", rows: [{ label: "2+ yrs study in regional AU", pts: "+5 pts" }] },
              { category: "Specialist Education", rows: [{ label: "Master's (research) or PhD in STEM", pts: "+10 pts" }] },
              { category: "Partner Skills", rows: [{ label: "Skilled partner (assessed + English)", pts: "+10 pts" }, { label: "No accompanying partner", pts: "+10 pts" }] },
              { category: "State Nomination (Subclass 190)", highlight: true, rows: [{ label: "State or territory nomination", pts: "+5 pts" }] },
            ].map((item) => (
              <div key={item.category}>
                <p className={`text-xs font-semibold uppercase tracking-widest mb-3 ${item.highlight ? "text-indigo-600" : "text-gray-400"}`}>{item.category}</p>
                <div className="space-y-1.5">
                  {item.rows.map((row) => (
                    <div key={row.label} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{row.label}</span>
                      <span className={`text-sm font-semibold ${item.highlight ? "text-indigo-600" : "text-gray-900"}`}>{row.pts}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 border-b border-blue-100" />
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-sm italic">
            Point values are indicative and may change. Always verify current allocations on the Department of Home Affairs website.
          </p>
        </div>
      </section>

      {/* FAQ SECTION */}
      <FAQSection />

      {/* CTA SECTION */}
      <CTA02 />

      {/* DISCLAIMER */}
      <section className="py-10 px-8 lg:px-20 xl:px-[120px] bg-white border-t border-gray-100">
        <div className="max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto">
          <p className="text-xs text-gray-400 leading-relaxed">
            <strong className="text-gray-500">Disclaimer:</strong> The information on this page is intended as general guidance only and does not constitute legal, migration, or professional advice. Immigration policies, processing times, fees, and occupation lists are subject to change by the Australian Government without notice. Always verify current requirements on the official Department of Home Affairs website (immi.homeaffairs.gov.au) or consult a registered migration agent (MARA) before making decisions about your visa application.
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
}
